import { Button } from '@/components/Buttons';
import { BUTTON_LABELS, FEATURE_FLAGS, TABLE_LABELS } from '@/constants';
import { IManagedUserRecentSummary, IManagedUserSession } from '@/types';
import { ITableColumn } from '@/components/Tables';
import { useAppRouting } from '@/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useGlobalStore } from '@/store/global-store';
import {
  ManagerTableCellActivityStatus,
  ManagerTableCellCertificationStatus,
  ManagerTableCellPracticeStatus,
  ManagerTableCellRoleplayStatus,
  ManagerTableCellUserInfo,
} from '@/components/Tables';

/**
 * Custom hook to manage the state and logic for the manager dashboard table,
 * including column definitions and unlock modal state.
 */
export function useManagerDashboardTable() {
  const { navigateToManagedUserSummary } = useAppRouting();
  const { managerActiveProduct, setManagerActiveUserScorecardIsLoading } = useGlobalStore();

  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [selectedUserForUnlock, setSelectedUserForUnlock] = useState<{
    userId: string;
    scenarioId: number;
  } | null>(null);

  const handleViewDetailsClick = useCallback(
    (record: IManagedUserRecentSummary) => {
      navigateToManagedUserSummary(record.userId);
    },
    [navigateToManagedUserSummary],
  );

  const handleViewScorecardClick = useCallback(
    (userId: string, data: IManagedUserSession) => {
      setManagerActiveUserScorecardIsLoading(true);
      navigateToManagedUserSummary(userId, { sessionId: data.sessionId.toString() });
    },
    [navigateToManagedUserSummary, setManagerActiveUserScorecardIsLoading],
  );

  const handleUnlockButtonClick = useCallback(
    (record: IManagedUserRecentSummary): void => {
      setUnlockModalOpen(true);
      setSelectedUserForUnlock({
        userId: record.userId,
        scenarioId: managerActiveProduct?.certificationId || 0,
      });
    },
    [managerActiveProduct?.certificationId],
  );

  const tableColumns: ITableColumn<IManagedUserRecentSummary>[] = useMemo(() => {
    return [
      {
        key: 'userName',
        title: TABLE_LABELS.SALES_REPRESENTATIVE,
        sortable: false,
        width: 250,
        render: (userName: string, record: IManagedUserRecentSummary) => (
          <ManagerTableCellUserInfo userName={userName} record={record} />
        ),
      },
      {
        key: 'mostRecentCertificate',
        title: TABLE_LABELS.CERTIFICATION_FINAL,
        sortable: true,
        width: 275,
        enabled: FEATURE_FLAGS.ENABLE_FINAL_CERTIFICATION,
        render: (
          mostRecentCertificate: IManagedUserSession | null,
          record: IManagedUserRecentSummary,
        ) => (
          <ManagerTableCellCertificationStatus
            mostRecentCertificate={mostRecentCertificate}
            onUnlockButtonClick={handleUnlockButtonClick}
            onViewScorecardClick={handleViewScorecardClick}
            record={record}
          />
        ),
      },
      {
        key: 'mostRecentPractice',
        title: TABLE_LABELS.PRACTICE_CERTIFICATION,
        sortable: false,
        width: 175,
        render: (mostRecentPractice: IManagedUserSession | null, record: IManagedUserRecentSummary) => (
          <ManagerTableCellPracticeStatus
            mostRecentPractice={mostRecentPractice}
            onViewScorecardClick={handleViewScorecardClick}
            record={record}
          />
        ),
      },
      {
        key: 'mostRecentRoleplay',
        title: TABLE_LABELS.SHARED_ROLEPLAY,
        sortable: false,
        width: 165,
        render: (mostRecentRoleplay: IManagedUserSession | null, record: IManagedUserRecentSummary) => (
          <ManagerTableCellRoleplayStatus
            mostRecentRoleplay={mostRecentRoleplay}
            onViewScorecardClick={handleViewScorecardClick}
            record={record}
          />
        ),
      },
      {
        key: 'mostRecentActivity',
        title: TABLE_LABELS.LAST_ACTIVITY,
        sortable: false,
        width: 135,
        render: (mostRecentActivity: string | null) => (
          <ManagerTableCellActivityStatus mostRecentActivity={mostRecentActivity} />
        ),
      },
      {
        key: 'actions',
        title: TABLE_LABELS.ACTIONS,
        headerClassName: 'opacity-0',
        sortable: false,
        render: (_, record: IManagedUserRecentSummary) => {
          return (
            <Button
              iconRight="ArrowRight"
              label={BUTTON_LABELS.VIEW_PROFILE}
              onClick={() => handleViewDetailsClick(record)}
              size="small"
              variant="secondary"
            />
          );
        },
      },
    ];
  }, [handleUnlockButtonClick, handleViewDetailsClick, handleViewScorecardClick]);

  return {
    tableColumns,
    unlockModalOpen,
    setUnlockModalOpen,
    selectedUserForUnlock,
    setSelectedUserForUnlock,
  };
}
